import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Typography, Card, Tabs } from "antd";
import { Link } from "react-router-dom";
import PeriodSelector from "../components/PeriodSelector";
import NestedHoldingsTable from "../charts/NestedHoldingsTable";
import SectorHeatmap from "../charts/SectorHeatmap";
import SECTOR_ROTATION from "../graphql/sector_rotation";
import { getHoldingPeriod } from "../utils/dateUtils";
import "../styles/Insights.css";
import "../styles/Tabs.css";

const { Title } = Typography;
const { TabPane } = Tabs;

const Sectors = () => {
  const defaultPeriod = getHoldingPeriod();
  const [holdingMonth, setHoldingMonth] = useState(defaultPeriod.holdingMonth);
  const [holdingYear, setHoldingYear] = useState(defaultPeriod.holdingYear);
  const [category, setCategory] = useState("All");


  const variables = useMemo(
    () => ({
      holdingMonth,
      holdingYear,
      category: category === "All" ? null : category,
    }),
    [holdingMonth, holdingYear, category]
  );

  const { data, loading, error } = useQuery(SECTOR_ROTATION, { 
    variables,
    onError: (err) => {
      console.error("GraphQL Error:", err);
      console.error("Network Error:", err.networkError);
      console.error("GraphQL Errors:", err.graphQLErrors);
    }
  });

  // Separate inflow and outflow sectors - only show sectors with score >= 1.0%
  // Updated threshold for percentage-weighted scoring (1.0% net change minimum)
  const inflowSectors = useMemo(() => {
    if (!data?.getSectorRotation) return [];
    return data.getSectorRotation
      .filter((sector) => sector.flowDirection === "INFLOW" && sector.flowScore >= 1.0)
      .map((sector) => ({
        sectorName: sector.sectorName,
        flowScore: sector.flowScore,
        holdingCount: sector.holdingCount,
        avgChangePerHolding: sector.avgChangePerHolding,
        funds: sector.funds,
      }));
  }, [data]);

  const outflowSectors = useMemo(() => {
    if (!data?.getSectorRotation) return [];
    return data.getSectorRotation
      .filter((sector) => sector.flowDirection === "OUTFLOW" && Math.abs(sector.flowScore) >= 1.0)
      .map((sector) => ({
        sectorName: sector.sectorName,
        flowScore: Math.abs(sector.flowScore),
        holdingCount: sector.holdingCount,
        avgChangePerHolding: Math.abs(sector.avgChangePerHolding),
        funds: sector.funds,
      }));
  }, [data]);

  // Prepare heatmap data - only include sectors with absolute score > 1.0%
  const heatmapData = useMemo(() => {
    if (!data?.getSectorRotation) return [];
    return data.getSectorRotation
      .filter((sector) => Math.abs(sector.flowScore) > 1.0)
      .map((sector) => ({
        sectorName: sector.sectorName,
        flowScore: sector.flowScore,
        flowDirection: sector.flowDirection,
      }));
  }, [data]);

  // Base columns for inflow table
  const baseColumnsInflow = [
    { title: "Sector", dataIndex: "sectorName", key: "sectorName" },
    {
      title: "Net Inflow (%)",
      dataIndex: "flowScore",
      key: "flowScore",
      render: (value) => <span style={{ color: "#52c41a", fontWeight: "bold" }}>+{value?.toFixed(2)}%</span>,
      sorter: (a, b) => b.flowScore - a.flowScore,
    },
    {
      title: "Holdings",
      dataIndex: "holdingCount",
      key: "holdingCount",
      render: (value) => <span>{value}</span>,
      sorter: (a, b) => b.holdingCount - a.holdingCount,
    },
    {
      title: "Avg per Holding",
      dataIndex: "avgChangePerHolding",
      key: "avgChangePerHolding",
      render: (value) => (
        <span style={{ color: value > 0 ? "#52c41a" : "#000" }}>
          {value > 0 ? "+" : ""}{value?.toFixed(2)}%
        </span>
      ),
      sorter: (a, b) => b.avgChangePerHolding - a.avgChangePerHolding,
    },
  ];

  // Nested columns for funds in inflow table
  const nestedColumnsInflow = [
    { 
      title: "Fund", 
      dataIndex: "mutualFundName", 
      key: "mutualFundName",
      render: (text, record) => (
        <Link 
          to={`/fund-details/${record.mutualFundId}`}
          style={{ color: "#1890ff", textDecoration: "none" }}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Total Change",
      dataIndex: "totalChangeInPtHoldings",
      key: "totalChangeInPtHoldings",
      render: (value) => (
        <span style={{ color: value > 0 ? "#52c41a" : value < 0 ? "#f5222d" : "#000" }}>
          {value > 0 ? "+" : ""}{value?.toFixed(2)}%
        </span>
      ),
    },
    {
      title: "Holdings",
      dataIndex: "holdingCount",
      key: "holdingCount",
      render: (value) => <span>{value}</span>,
    },
    {
      title: "Avg per Holding",
      dataIndex: "avgChangePerHolding",
      key: "avgChangePerHolding",
      render: (value) => (
        <span style={{ color: value > 0 ? "#52c41a" : value < 0 ? "#f5222d" : "#000" }}>
          {value > 0 ? "+" : ""}{value?.toFixed(2)}%
        </span>
      ),
    },
  ];

  // Base columns for outflow table
  const baseColumnsOutflow = [
    { title: "Sector", dataIndex: "sectorName", key: "sectorName" },
    {
      title: "Net Outflow (%)",
      dataIndex: "flowScore",
      key: "flowScore",
      render: (value) => <span style={{ color: "#f5222d", fontWeight: "bold" }}>-{value?.toFixed(2)}%</span>,
      sorter: (a, b) => b.flowScore - a.flowScore,
    },
    {
      title: "Holdings",
      dataIndex: "holdingCount",
      key: "holdingCount",
      render: (value) => <span>{value}</span>,
      sorter: (a, b) => b.holdingCount - a.holdingCount,
    },
    {
      title: "Avg per Holding",
      dataIndex: "avgChangePerHolding",
      key: "avgChangePerHolding",
      render: (value) => (
        <span style={{ color: "#f5222d" }}>
          -{value?.toFixed(2)}%
        </span>
      ),
      sorter: (a, b) => b.avgChangePerHolding - a.avgChangePerHolding,
    },
  ];

  // Nested columns for funds in outflow table
  const nestedColumnsOutflow = [
    { 
      title: "Fund", 
      dataIndex: "mutualFundName", 
      key: "mutualFundName",
      render: (text, record) => (
        <Link 
          to={`/fund/${record.mutualFundId}`}
          style={{ color: "#1890ff", textDecoration: "none" }}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Total Change",
      dataIndex: "totalChangeInPtHoldings",
      key: "totalChangeInPtHoldings",
      render: (value) => (
        <span style={{ color: value > 0 ? "#52c41a" : value < 0 ? "#f5222d" : "#000" }}>
          {value > 0 ? "+" : ""}{value?.toFixed(2)}%
        </span>
      ),
    },
    {
      title: "Holdings",
      dataIndex: "holdingCount",
      key: "holdingCount",
      render: (value) => <span>{value}</span>,
    },
    {
      title: "Avg per Holding",
      dataIndex: "avgChangePerHolding",
      key: "avgChangePerHolding",
      render: (value) => (
        <span style={{ color: value > 0 ? "#52c41a" : value < 0 ? "#f5222d" : "#000" }}>
          {value > 0 ? "+" : ""}{value?.toFixed(2)}%
        </span>
      ),
    },
  ];

  const renderSection = (title, baseColumns, nestedColumns, data, loading) => (
    <div className="insights-section">
      <Card className="insights-section-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <div className="insights-section-header">
          <Title level={4} className="insights-section-title">
            {title}
          </Title>
        </div>
        <NestedHoldingsTable
          baseColumns={baseColumns}
          nestedColumns={nestedColumns}
          data={data || []}
          loading={loading}
        />
      </Card>
    </div>
  );


  return (
    <div className="insights-page">
      <div className="insights-header">
        <div className="insights-header-left">
          <Title level={3} className="insights-header-title">
            Sector Rotation
          </Title>
          <span className="insights-header-subtitle">
            Track sectors where funds are moving in or out, based on monthly changes.
          </span>
        </div>
      </div>

      <div className="tabs-container">
        <Tabs
          activeKey={category}
          onChange={(key) => setCategory(key)}
          tabBarGutter={24}
          className="tabs-control"
        >
          <TabPane tab="All" key="All" />
          <TabPane tab="Large Cap" key="Large Cap" />
          <TabPane tab="Large and Mid" key="Large and Mid" />
          <TabPane tab="Mid Cap" key="Mid Cap" />
          <TabPane tab="Small Cap" key="Small Cap" />
          <TabPane tab="Flexi Cap" key="Flexi Cap" />
          <TabPane tab="Momentum Funds" key="Momentum Funds" />
          <TabPane tab="Multi-Cap" key="Multi-Cap" />
        </Tabs>
      </div>

      <div className="insights-filter-group insights-filter-group--top">
        <PeriodSelector
          holdingMonth={holdingMonth}
          holdingYear={holdingYear}
          setHoldingMonth={setHoldingMonth}
          setHoldingYear={setHoldingYear}
        />
      </div>

      {error ? (
        <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
          Error loading data: {error.message}
        </div>
      ) : (
        <>
          {!loading && heatmapData.length > 0 && (
            <div className="insights-section">
              <Card className="insights-section-card" bordered={false} bodyStyle={{ padding: 0 }}>
                <SectorHeatmap data={heatmapData} title="Sector Rotation Heatmap" />
              </Card>
            </div>
          )}

          {!loading && heatmapData.length === 0 && (
            <div className="insights-section">
              <Card className="insights-section-card" bordered={false}>
                <div style={{ padding: "20px", textAlign: "center" }}>
                  No significant sector rotation data available for this period (showing sectors with net flow &gt; 1%)
                </div>
              </Card>
            </div>
          )}

          {renderSection("Sectors with Net Inflow", baseColumnsInflow, nestedColumnsInflow, inflowSectors, loading)}

          {renderSection("Sectors with Net Outflow", baseColumnsOutflow, nestedColumnsOutflow, outflowSectors, loading)}
        </>
      )}
    </div>
  );
};

export default Sectors;
